import { getAnalytics } from "@tm/insurance/analytics";
import { trackEvent, triggerPageExit } from "@tm/insurance/analytics/xdm";
import { navigationActions } from "@tm/insurance/navigation";
import { PARTNER } from "@tm/isomorphic-utils";
import { BlueChev } from "@tm/static/icons";
import { TextNodeType } from "@tm/ui-widgets/components";
import { usePlanType } from "@tm/ui/contexts";
import {
  IntersectionObserverData,
  useIntersectionObserver,
} from "@tm/ui/hooks";
import { useCallback, useRef, useState } from "react";
import { IPlan } from "../../pages/InsuranceCommonLanding/types";
import EducationModal from "../EducationModal/EducationModal";
import FlexComp from "../FlexComp/FlexComp";
import FormattedText from "../FormattedText/FormattedText";
import {
  ArrowClip,
  Chev,
  ChevBorder,
  ChevStateWrap,
  DiscoveryBarArrowColor,
  DiscoveryBarSection,
  PlanCard,
  PlanCards,
  PlanDetails,
  PlanIconV2,
  PlanSeparator,
  PlanTitleContainer,
  SectionIcon,
  SectionTitle,
  SectionTitleRow,
  SectionTitleTextWrap,
  TagContainer,
  TagImage,
  TravelProtectionV2Wrapper,
} from "./TravelProtectionV2.style";

interface TravelProtectionV2Props {
  title: TextNodeType;
  icon?: string;
  plans: IPlan[];
  discoveryText?: TextNodeType;
  isFederated?: boolean;
  partnerType?: string;
  discoveryBarArrowColor?: DiscoveryBarArrowColor;
}

const TravelProtectionV2 = ({
  title,
  icon,
  plans,
  discoveryText,
  isFederated,
  partnerType,
  discoveryBarArrowColor,
}: TravelProtectionV2Props) => {
  if (!title || !plans) return null;

  const isGI = partnerType === PARTNER.GI;
  const resolvedArrowColor =
    discoveryBarArrowColor ?? (isGI ? "black" : "white");

  const { setType } = usePlanType();
  const [activePlan, setActivePlan] = useState<IPlan | null>(plans[0] || null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const analytics = getAnalytics();
  const hasTracked = useRef<boolean>(false);

  // Track component view when it comes into viewport
  const handleIntersection = useCallback((data: IntersectionObserverData) => {
    if (!hasTracked.current) {
      analytics?.trackCommonLandingEvents({
        actionType: "travel_protection_v2_seen",
      });
    }
    hasTracked.current = true;
  }, []);

  const { ref: travelProtectionRef } = useIntersectionObserver(
    handleIntersection,
    {
      threshold: 0.5, // Trigger when 50% of component is visible
      triggerOnLeaveViewport: true,
    }
  );

  // Education modal tracking handlers
  const handleEducationModalShown = () => {
    analytics?.trackLandingEvents?.({ actionType: "educationModal_shown" });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "educationModal_shown",
      },
    });
  };
  const handleEducationModalNextClick = () => {
    analytics?.trackLandingEvents?.({
      actionType: "educationModal_next_click",
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "educationModal_next_click",
      },
    });
  };
  const handleEducationModalSkipClick = () => {
    analytics?.trackLandingEvents?.({
      actionType: "educationModal_skip_click",
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "educationModal_skip_click",
      },
    });
  };
  const handleEducationModalPageShown = (pageId: string) => {
    analytics?.trackLandingEvents?.({
      actionType: `educationModal_${pageId}_shown`,
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: `educationModal_${pageId}_shown`,
      },
    });
  };

  const handleEducationSkip = () => {
    setShowModal(false);
    setType(activePlan?.type);
    navigationActions.navigateToLanding({ type: activePlan?.type }, false);
  };

  const handlePlanClick = (plan: IPlan) => {
    analytics?.trackCommonLandingEvents({
      actionType: `protection_plan_${plan.type}_clicked`,
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: `protection_plan_${plan.type}_clicked`,
      },
    });
    triggerPageExit();
    if (plan.showPages) {
      setActivePlan(plan);
      setCurrentPageIndex(0);
      setShowModal(true);
    } else {
      setType(plan.type);
      if (
        isFederated ||
        (typeof window !== "undefined" &&
          window.location.pathname.includes("/travel-insurance"))
      ) {
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set("type", plan?.type);
        window.location.href = `${
          window.location.origin
        }/insurance?${queryParams.toString()}`;
      } else {
        navigationActions.navigateToLanding({ type: plan?.type }, false);
      }
    }
  };

  return (
    <>
      <TravelProtectionV2Wrapper ref={travelProtectionRef}>
        {discoveryText && (
          <DiscoveryBarSection
            $showArrows={true}
            $arrowColor={resolvedArrowColor}
          >
            <FormattedText value={discoveryText} />
          </DiscoveryBarSection>
        )}
        <FlexComp>
          <SectionTitle>
            <SectionTitleRow>
              <SectionTitleTextWrap>
                <FormattedText value={title} />
                <SectionIcon src={icon} alt={"travelProtection"} />
              </SectionTitleTextWrap>
              <ArrowClip>
                <ChevBorder />
                <Chev />
              </ArrowClip>
            </SectionTitleRow>
          </SectionTitle>
          <PlanCards>
            {plans.map((plan, index) => (
              <>
                <PlanCard key={plan.type} onClick={() => handlePlanClick(plan)}>
                  <PlanIconV2 src={plan.icon} alt={"plan icon"} />
                  <PlanDetails>
                    <PlanTitleContainer>
                      <FormattedText value={plan.title} />
                      {plan.tagURL && (
                        <TagContainer>
                          <TagImage src={plan.tagURL} alt="New" />
                        </TagContainer>
                      )}
                    </PlanTitleContainer>
                    {plan.info && (
                      <FormattedText
                        value={plan.info}
                        baseStyle={{ fontSize: "12px", color: "#4a4a4a" }}
                      />
                    )}
                  </PlanDetails>
                  <ChevStateWrap>
                    <BlueChev />
                  </ChevStateWrap>
                </PlanCard>
                {index < plans.length - 1 && <PlanSeparator />}
              </>
            ))}
          </PlanCards>
        </FlexComp>
      </TravelProtectionV2Wrapper>
      {showModal && activePlan && activePlan.showPages && (
        <EducationModal
          pagesData={activePlan.pages}
          knowMore={activePlan.knowMore}
          setShowEducationModal={setShowModal}
          skipButtonHandler={handleEducationSkip}
          onModalShown={handleEducationModalShown}
          onNextClick={handleEducationModalNextClick}
          onSkipClick={handleEducationModalSkipClick}
          onPageShown={handleEducationModalPageShown}
        />
      )}
    </>
  );
};

export default TravelProtectionV2;
